import { useState } from "react";
import {
    ArrowRight,
    Check,
    FileText,
    Lock,
    Network,
    Quote,
    ScrollText,
    Shield,
    Sparkles,
    X,
} from "lucide-react";
import { clientApi } from "@/lib/clientAuth";

/* eslint-disable react/no-unescaped-entities */

// ─────────────────────────────────────────────────────────────
// Client onboarding tour — premium modal overlay shown the first
// time a client logs into their private space. Saves completion
// via POST /api/client/auth/tour-complete.
// ─────────────────────────────────────────────────────────────

const STEPS = [
    {
        icon: Quote,
        kicker: "Bienvenue",
        title: "Votre espace privé XV Capital Partners.",
        body: "Un espace confidentiel — pensé pour suivre votre dossier en toute discrétion. Aucune information n'en sort sans votre accord.",
    },
    {
        icon: FileText,
        kicker: "Étape 1",
        title: "La synthèse stratégique de votre projet.",
        body: "Vous pourrez la rédiger une fois — elle restera ensuite votre boussole, partagée avec le cabinet. Soyez bref et précis.",
    },
    {
        icon: Sparkles,
        kicker: "Étape 2",
        title: "L'avancement de votre opération.",
        body: "Suivez en temps réel chaque étape du dossier. Lorsqu'un compte rendu est validé, il devient visible dans la timeline.",
    },
    {
        icon: Network,
        kicker: "Étape 3",
        title: "Expertises mobilisées & réflexions stratégiques.",
        body: "Les cartes ci-dessous résument les compétences engagées sur votre dossier et les questions clés en cours d'instruction.",
    },
    {
        icon: Check,
        kicker: "Prêt",
        title: "Le cabinet est à vos côtés.",
        body: "Pour toute question, contactez votre interlocuteur. Discrétion absolue. Engagement total.",
    },
];

export function ClientOnboardingTour({ onClose }) {
    const [idx, setIdx] = useState(0);
    const [busy, setBusy] = useState(false);
    const step = STEPS[idx];
    const Icon = step.icon;
    const isLast = idx === STEPS.length - 1;

    const finish = async () => {
        setBusy(true);
        try {
            await clientApi.post("/auth/tour-complete");
        } catch {
            // Non-blocking — close anyway
        } finally {
            setBusy(false);
            onClose?.();
        }
    };

    return (
        <div
            data-testid="client-onboarding-tour"
            className="fixed inset-0 z-[80] flex items-center justify-center p-6"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#070d18]/92 backdrop-blur-md"
                aria-hidden="true"
            />

            {/* Card */}
            <div className="relative w-full max-w-2xl bg-[#0b1220] border border-[#d4af37]/25 shadow-2xl">
                {/* Skip */}
                <button
                    type="button"
                    onClick={finish}
                    data-testid="tour-skip"
                    aria-label="Passer la présentation"
                    className="absolute top-5 right-5 text-[#f4ecd8]/40 hover:text-[#d4af37] transition-colors duration-500"
                >
                    <X strokeWidth={1.2} size={18} />
                </button>

                {/* Progress dots */}
                <div className="flex items-center gap-2 px-10 pt-10">
                    {STEPS.map((_, i) => (
                        <span
                            key={i}
                            className={`block h-[2px] transition-all duration-500 ${
                                i === idx
                                    ? "w-10 bg-[#d4af37]"
                                    : i < idx
                                      ? "w-6 bg-[#d4af37]/60"
                                      : "w-6 bg-[#f4ecd8]/10"
                            }`}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="px-10 pt-10 pb-12">
                    <div className="flex items-center gap-4 mb-7">
                        <span className="inline-flex items-center justify-center w-12 h-12 border border-[#d4af37]/30 text-[#d4af37]">
                            <Icon strokeWidth={1.1} size={18} />
                        </span>
                        <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/85">
                            {step.kicker}
                        </span>
                    </div>
                    <h2
                        data-testid={`tour-step-${idx}`}
                        className="font-serif text-[#f4ecd8] text-[28px] sm:text-[32px] leading-tight mb-6"
                        style={{ fontWeight: 300 }}
                    >
                        {step.title}
                    </h2>
                    <p className="font-serif text-[15px] sm:text-[16px] text-[#f4ecd8]/75 leading-[1.85] font-light italic">
                        {step.body}
                    </p>

                    <div className="mt-10 pt-6 border-t border-white/[0.06] flex items-center justify-between gap-4">
                        <span className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/40 tabular-nums">
                            {idx + 1} / {STEPS.length}
                        </span>
                        <div className="flex items-center gap-3">
                            {idx > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setIdx((i) => Math.max(0, i - 1))}
                                    className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/55 hover:text-[#f4ecd8] transition-colors duration-500"
                                >
                                    Précédent
                                </button>
                            )}
                            {!isLast ? (
                                <button
                                    type="button"
                                    onClick={() => setIdx((i) => i + 1)}
                                    data-testid="tour-next"
                                    className="group inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                                >
                                    Suivant
                                    <ArrowRight
                                        strokeWidth={1.2}
                                        size={13}
                                        className="transition-transform duration-500 group-hover:translate-x-1"
                                    />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={finish}
                                    disabled={busy}
                                    data-testid="tour-finish"
                                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#d4af37] text-[#070d18] text-[10px] tracking-[0.3em] uppercase hover:bg-[#f4ecd8] transition-all duration-500 disabled:opacity-50"
                                >
                                    {busy ? "Activation…" : "Entrer dans mon espace"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// ClientTermsConsent — overlay d'acceptation CGV/CGU à la
// première connexion. La case « ne plus afficher » persiste
// l'acceptation côté serveur ; sinon, masquage session-only.
// ─────────────────────────────────────────────────────────────
export function ClientTermsConsent({ onClose }) {
    const [accepted, setAccepted] = useState(false);
    const [dontShow, setDontShow] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (!accepted) {
            setErr("Veuillez accepter les CGU et CGV pour continuer.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            if (dontShow) {
                await clientApi.post("/auth/accept-terms");
            } else {
                // Session-only dismissal
                try {
                    sessionStorage.setItem("xv_terms_dismissed", "1");
                } catch {
                    /* SSR safe */
                }
            }
            onClose?.(dontShow);
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Impossible d'enregistrer votre acceptation.",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="client-terms-consent"
            className="fixed inset-0 z-[90] flex items-center justify-center p-6"
        >
            <div
                className="absolute inset-0 bg-[#070d18]/95 backdrop-blur-md"
                aria-hidden="true"
            />
            <div className="relative w-full max-w-xl bg-[#0b1220] border border-[#d4af37]/30 shadow-2xl">
                <div className="px-9 pt-10 pb-9">
                    <div className="flex items-center gap-4 mb-7">
                        <span className="inline-flex items-center justify-center w-12 h-12 border border-[#d4af37]/40 text-[#d4af37]">
                            <ScrollText strokeWidth={1.1} size={18} />
                        </span>
                        <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/85">
                            Conditions contractuelles
                        </span>
                    </div>
                    <h2
                        className="font-serif text-[#f4ecd8] text-[26px] sm:text-[30px] leading-tight mb-5"
                        style={{ fontWeight: 300 }}
                    >
                        Avant d'accéder à votre espace.
                    </h2>
                    <p className="text-[14px] sm:text-[15px] text-[#f4ecd8]/75 leading-[1.85] font-light italic mb-7">
                        Pour des raisons de transparence, nous vous invitons
                        à prendre connaissance des Conditions Générales
                        d'Utilisation et des Conditions Générales de Vente
                        applicables à votre accompagnement.
                    </p>

                    {/* Links */}
                    <div className="flex flex-wrap items-center gap-5 mb-7 text-[11px] tracking-[0.26em] uppercase">
                        <a
                            href="/cgu"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="terms-link-cgu"
                            className="inline-flex items-center gap-2 text-[#d4af37]/85 hover:text-[#d4af37] transition-colors duration-500"
                        >
                            <FileText strokeWidth={1.3} size={11} />
                            Lire les CGU
                        </a>
                        <a
                            href="/confidentialite"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="terms-link-privacy"
                            className="inline-flex items-center gap-2 text-[#d4af37]/85 hover:text-[#d4af37] transition-colors duration-500"
                        >
                            <Shield strokeWidth={1.3} size={11} />
                            Confidentialité
                        </a>
                    </div>

                    {/* Checkboxes */}
                    <label
                        data-testid="terms-accept-checkbox"
                        className="flex items-start gap-3 cursor-pointer mb-4 group"
                    >
                        <input
                            type="checkbox"
                            checked={accepted}
                            onChange={(e) => setAccepted(e.target.checked)}
                            className="mt-1 accent-[#d4af37] w-4 h-4 cursor-pointer"
                        />
                        <span className="text-[14px] font-serif text-[#f4ecd8]/85 leading-relaxed group-hover:text-[#f4ecd8] transition-colors duration-500">
                            J'ai pris connaissance et j'accepte les{" "}
                            <strong className="text-[#d4af37]">Conditions Générales d'Utilisation</strong>{" "}
                            ainsi que les{" "}
                            <strong className="text-[#d4af37]">Conditions Générales de Vente</strong>{" "}
                            du cabinet.
                        </span>
                    </label>

                    <label
                        data-testid="terms-dont-show-checkbox"
                        className="flex items-start gap-3 cursor-pointer mb-7 group"
                    >
                        <input
                            type="checkbox"
                            checked={dontShow}
                            onChange={(e) => setDontShow(e.target.checked)}
                            className="mt-1 accent-[#d4af37] w-4 h-4 cursor-pointer"
                        />
                        <span className="text-[13px] text-[#f4ecd8]/55 leading-relaxed italic group-hover:text-[#f4ecd8]/75 transition-colors duration-500">
                            Ne plus afficher ce message lors de mes
                            prochaines connexions.
                        </span>
                    </label>

                    {err && (
                        <div className="text-[12.5px] text-[#e89090] border-l-2 border-[#e89090] pl-3 py-1 mb-5">
                            {err}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2 border-t border-white/[0.06]">
                        <button
                            type="button"
                            data-testid="terms-accept"
                            onClick={submit}
                            disabled={busy || !accepted}
                            className="inline-flex items-center gap-3 px-7 py-3 bg-[#d4af37] text-[#070d18] text-[10px] tracking-[0.3em] uppercase hover:bg-[#f4ecd8] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy
                                ? "Enregistrement…"
                                : "Continuer vers mon espace"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────
// ProjectSynthesisCard — client-side strategic synthesis.
// Editable once, then locked forever (read-only display).
// ─────────────────────────────────────────────────────────────
export function ProjectSynthesisCard({ synthesis, onSaved }) {
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const locked = !!synthesis?.locked;

    const submit = async (e) => {
        e.preventDefault();
        if (locked) return;
        const text = body.trim();
        if (text.length < 20) {
            setErr("Minimum 20 caractères requis.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const { data } = await clientApi.post(
                "/dossier/project-synthesis",
                { body: text },
            );
            onSaved?.(data.project_synthesis);
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Impossible d'enregistrer la synthèse.",
            );
        } finally {
            setBusy(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            });
        } catch {
            return iso;
        }
    };

    return (
        <section
            data-testid="client-project-synthesis"
            className="bg-white/[0.02] border border-white/[0.06] p-7 lg:p-9 mb-10"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                        <FileText strokeWidth={1.1} size={11} />
                        Synthèse stratégique du projet
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        {locked
                            ? "Votre boussole pour cette mission."
                            : "Rédigez votre boussole pour cette mission."}
                    </h2>
                </div>
                {locked && (
                    <span
                        data-testid="synthesis-locked-badge"
                        className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/85"
                    >
                        <Lock strokeWidth={1.2} size={11} />
                        Verrouillée le {formatDate(synthesis.locked_at)}
                    </span>
                )}
            </div>

            {locked ? (
                <div className="bg-[#070d18] border border-white/[0.05] p-6 lg:p-8">
                    <p
                        data-testid="synthesis-locked-body"
                        className="font-serif text-[15px] text-[#f4ecd8]/90 leading-[1.95] whitespace-pre-wrap italic"
                    >
                        {synthesis.body}
                    </p>
                    <p className="mt-5 pt-4 border-t border-white/[0.06] text-[11px] tracking-[0.22em] uppercase text-[#d4af37]/55">
                        Cette synthèse a été rédigée par vos soins. Pour la
                        faire évoluer, échangez avec votre interlocuteur XV.
                    </p>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4">
                    <p className="text-[13px] text-[#f4ecd8]/55 italic font-light leading-relaxed">
                        En 4 à 6 phrases : votre vision, l'enjeu central, ce
                        que vous attendez du cabinet. Cette synthèse sera
                        verrouillée après enregistrement.
                    </p>
                    <textarea
                        data-testid="synthesis-input"
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Ma vision pour cette opération…"
                        className="w-full bg-[#070d18] border border-white/15 focus:border-[#d4af37] outline-none p-4 text-[14px] font-serif italic text-[#f4ecd8] leading-relaxed resize-none"
                    />
                    {err && (
                        <div className="text-[12.5px] text-[#e89090] border-l-2 border-[#e89090] pl-3 py-1">
                            {err}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-[10.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/35">
                            {body.length} / 6000 caractères
                        </span>
                        <button
                            type="submit"
                            disabled={busy || body.trim().length < 20}
                            data-testid="synthesis-submit"
                            className="inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                        >
                            <Lock strokeWidth={1.2} size={12} />
                            {busy ? "Verrouillage…" : "Verrouiller ma synthèse"}
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
}
